import * as React from 'react';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogProps,
    DialogTitle,
    IconButton,
    Stack,
    Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HfButton, { HfButtonIntent } from '@root/app/shared/new-components/hf-button/HfButton';
import { HF_NEUTRAL_COLORS, HF_SPACING } from '@root/app/shared/theme/designTokens';

export type HfDialogSize = 'sm' | 'md' | 'lg';

export interface HfDialogAction {
    /** Visible label. */
    label: React.ReactNode;
    /** Click handler. */
    onClick: () => void;
    /** Visual intent — defaults to `primary`. */
    intent?: HfButtonIntent;
    /** Disable the button. */
    disabled?: boolean;
    /** Loading state — disables and dims while in flight. */
    loading?: boolean;
}

export interface HfDialogProps extends Omit<DialogProps, 'open' | 'onClose' | 'title' | 'maxWidth'> {
    /** Controls visibility. */
    open: boolean;
    /** Close handler (X icon, backdrop click, escape). */
    onClose: () => void;
    /** Dialog title rendered in the header. */
    title: React.ReactNode;
    /** Optional subtitle / description under the title. */
    subtitle?: React.ReactNode;
    /** Optional left-aligned secondary action (e.g. "Cancel"). */
    secondaryAction?: HfDialogAction;
    /** Primary action (e.g. "Save"). */
    primaryAction?: HfDialogAction;
    /** Size preset; maps to a maxWidth. Defaults to `sm`. */
    size?: HfDialogSize;
    /** Hide the close (X) button in the header. */
    hideCloseButton?: boolean;
    /** Render arbitrary footer content instead of the default primary/secondary actions. */
    footer?: React.ReactNode;
    /** Body content. */
    children?: React.ReactNode;
}

const SIZE_MAX_WIDTH: Record<HfDialogSize, DialogProps['maxWidth']> = {
    sm: 'sm',
    md: 'md',
    lg: 'lg'
};

/**
 * Design-system Dialog / Modal.
 *
 * Standardises the title + close-button header, optional subtitle,
 * body content and a primary/secondary action footer. Built on MUI
 * `<Dialog>` so it inherits the theme's radius + shadow + divider
 * overrides.
 */
export const HfDialog: React.FC<HfDialogProps> = ({
    open,
    onClose,
    title,
    subtitle,
    secondaryAction,
    primaryAction,
    size = 'sm',
    hideCloseButton,
    footer,
    children,
    fullWidth = true,
    ...rest
}) => (
    <Dialog
        open={open}
        onClose={onClose}
        maxWidth={SIZE_MAX_WIDTH[size]}
        fullWidth={fullWidth}
        {...rest}
    >
        <DialogTitle sx={{ pr: hideCloseButton ? undefined : `${HF_SPACING.huge}px` }}>
            <Stack direction='row' alignItems='flex-start' justifyContent='space-between'>
                <Stack spacing={subtitle ? 0.5 : 0}>
                    <Typography component='span' variant='h2'>{title}</Typography>
                    {subtitle && (
                        <Typography variant='body3' sx={{ color: HF_NEUTRAL_COLORS[500] }}>
                            {subtitle}
                        </Typography>
                    )}
                </Stack>
                {!hideCloseButton && (
                    <IconButton
                        aria-label='close'
                        size='small'
                        onClick={onClose}
                        sx={{ color: HF_NEUTRAL_COLORS[500], mt: '-4px', mr: '-8px' }}
                    >
                        <CloseIcon fontSize='small' />
                    </IconButton>
                )}
            </Stack>
        </DialogTitle>
        <DialogContent>
            {typeof children === 'string' ? (
                <DialogContentText>{children}</DialogContentText>
            ) : (
                children
            )}
        </DialogContent>
        {(footer || primaryAction || secondaryAction) && (
            <DialogActions>
                {footer ?? (
                    <>
                        {secondaryAction && (
                            <HfButton
                                intent={secondaryAction.intent ?? 'ghost'}
                                onClick={secondaryAction.onClick}
                                disabled={secondaryAction.disabled || secondaryAction.loading}
                            >
                                {secondaryAction.label}
                            </HfButton>
                        )}
                        {primaryAction && (
                            <HfButton
                                intent={primaryAction.intent ?? 'primary'}
                                onClick={primaryAction.onClick}
                                disabled={primaryAction.disabled || primaryAction.loading}
                            >
                                {primaryAction.label}
                            </HfButton>
                        )}
                    </>
                )}
            </DialogActions>
        )}
    </Dialog>
);

export default HfDialog;
